import React from 'react';
import { useQuery, UseQueryResult } from 'react-query';
import { getSectionUnitMeta } from '@apis/sections';
import { useParams } from 'react-router-dom';
import {
  getLessonDetail,
  getLessonOER,
  getStandard,
  getLessonStandard,
} from '@apis/curriculum';
import {
  getAllLearnosityAdaptivePracticeQuestions,
  getLearnosityAdaptivePracticeQuestions,
  getLearnosityDistrictSelectedQuestions,
} from '@apis/practice';
import { useCourseById, useCurrentCourse } from '@reducers/courses/hooks';
import {
  convertQuestionToCamelCase,
  Question,
} from '@learnosityassessment/AssessmentServiceAPI';

import {
  EducationalResourceResponse,
  LessonDetailsResponseType,
  unitMetaDataResponse,
} from '../../Types';

export const useGetLessonDetail = (lessonGroupId?: string | number) => {
  const params = useParams();
  // Despite being labeled as 'lessonId' in the router params, it actually represents the lessonGroupId.
  const effectiveLessonGroupId = lessonGroupId || params.lessonId;
  // The selected course in the store can still be the previous one right after login, so the
  // course in the URL wins.
  const routeCourse = useCourseById(params.courseId);
  const currentCourse = useCurrentCourse();
  const curriculumId = routeCourse?.curriculum ?? currentCourse?.curriculum;

  const queryResponse = useQuery<LessonDetailsResponseType>(
    ['getLessonDetail', effectiveLessonGroupId, params.sectionId, curriculumId],
    async () => {
      const { response } = (await getLessonDetail(effectiveLessonGroupId, curriculumId)) as unknown as {
        response: { data: LessonDetailsResponseType };
      };

      return response?.data || {};
    },
    {
      useErrorBoundary: true,
      suspense: true,
      enabled: !!params.sectionId && !!effectiveLessonGroupId,
      staleTime: 1000 * 60 * 60 * 24,
    },
  );

  // Kept as a widened `UseQueryResult` rather than `{ data }` so callers can also read
  // the settled/loading flags. `useAreTabConditionsResolved` depends on them.
  return queryResponse as UseQueryResult<LessonDetailsResponseType>;
};

export const useGetEducationalResources = () => {
  const { lessonId: lessonGroupId } = useParams();
  const queryResponse = useQuery<EducationalResourceResponse>(
    ['getLessonOER', lessonGroupId],
    async () => {
      const { response } = (await getLessonOER(lessonGroupId)) as unknown as {
        response: { data: EducationalResourceResponse };
      };

      return response?.data || [];
    },
    {
      useErrorBoundary: true,
      suspense: true,
      enabled: !!lessonGroupId,
      staleTime: 1000 * 60 * 60 * 24,
    },
  );

  return queryResponse;
};

export const useGetStandards = () => {
  const { sectionId } = useParams();

  const queryResponse = useQuery<any>(
    ['getStandards'],
    async () => {
      const { response } = (await getStandard()) as unknown as {
        response: { data: any };
      };

      return response?.data || {};
    },
    {
      useErrorBoundary: true,
      suspense: true,
      enabled: !!sectionId,
      staleTime: 1000 * 60 * 60 * 24,
    },
  );

  return queryResponse;
};

export const useGetSectionUnitMeta = () => {
  const { sectionId, subjectId } = useParams();

  const queryResponse = useQuery<unitMetaDataResponse>(
    ['getSectionUnitMeta', sectionId, subjectId],
    async () => {
      const { response } = (await getSectionUnitMeta(
        sectionId,
        subjectId,
      )) as unknown as {
        response: { data: unitMetaDataResponse };
      };

      return response?.data || {};
    },
    {
      placeholderData: [],
      useErrorBoundary: true,
      suspense: true,
      enabled: !!sectionId,
      staleTime: 1000 * 60 * 60 * 24,
    },
  );

  return queryResponse;
};

export const useCurrentSubject = () => {
  const { subjectId } = useParams();
  const { data: sectionMeta } = useGetSectionUnitMeta();

  const currentSubject = React.useMemo(() => {
    if (!sectionMeta || Object.keys(sectionMeta).length === 0) {
      return undefined;
    }
    return sectionMeta?.find((subject) => subject.id === parseInt(subjectId, 10));
  }, [sectionMeta, subjectId]);

  return currentSubject;
};

export const useCurrentUnit = () => {
  const { unitId } = useParams();
  const subject = useCurrentSubject();

  const currentUnit = React.useMemo(() => {
    if (!subject) {
      return undefined;
    }
    return subject?.units?.find((unit) => unit.id === parseInt(unitId, 10));
  }, [subject, unitId]);
  return currentUnit;
};

export const useGetLessonStandard = () => {
  const { lessonId, sectionId } = useParams();
  const courseData = useCurrentCourse();
  const standardId = courseData?.syllabus ?? 0;

  const queryResponse = useQuery<any>(
    ['getLessonStandard', lessonId, standardId],
    async () => {
      const { response } = (await getLessonStandard(lessonId, standardId)) as unknown as {
        response: { data: any };
      };

      return response?.data || {};
    },
    {
      useErrorBoundary: true,
      suspense: true,
      enabled: !!sectionId && !!lessonId,
      staleTime: 1000 * 60 * 60 * 24,
    },
  );

  return queryResponse;
};
export const useGetAllLearnosityAdaptivePracticeQuestions = (
  lessonId?: string | number,
  options?: {
    [key: string]: any;
  },
) => {
  const params = useParams();

  const effectiveLessonId = lessonId || params.lessonId;

  const queryResponse = useQuery<Array<Question>>(
    ['getAllLearnosityAdaptivePracticeQuestions', effectiveLessonId],
    async () => {
      const { response } = (await getAllLearnosityAdaptivePracticeQuestions(
        effectiveLessonId,
      )) as unknown as {
        response: { data: Array<Question> };
      };

      return response?.data?.map(convertQuestionToCamelCase) || [];
    },
    {
      useErrorBoundary: true,
      suspense: false,
      enabled: !!params.sectionId && !!effectiveLessonId,
      staleTime: 1000 * 60 * 60 * 24,
      ...options,
    },
  );

  return queryResponse;
};

export const useGetLearnosityAdaptivePracticeQuestions = (
  lessonId?: string | number,
  options?: {
    [key: string]: any;
  },
) => {
  const params = useParams();

  const effectiveLessonId = lessonId || params.lessonId;

  const queryResponse = useQuery<Array<Question>>(
    ['getLearnosityAdaptivePracticeQuestions', effectiveLessonId],
    async () => {
      const { response } = (await getLearnosityAdaptivePracticeQuestions(
        effectiveLessonId,
      )) as unknown as {
        response: { data: Array<Question> };
      };

      return response?.data?.map(convertQuestionToCamelCase) || [];
    },
    {
      useErrorBoundary: true,
      suspense: true,
      enabled: !!params.sectionId && !!effectiveLessonId,
      staleTime: 1000 * 60 * 60 * 24,
      ...options,
    },
  );

  return queryResponse;
};

export const useDistrictSelectedQuestions = () => {
  const { lessonId: lessonGroupId, courseId } = useParams();
  const courseDetails = useCourseById(courseId);
  const districtId = courseDetails?.organization?.district?.id;
  const queryResponse = useQuery<Question[]>(
    ['districtSelectedQuestions', lessonGroupId, districtId],
    async () => {
      const { response } = (await getLearnosityDistrictSelectedQuestions(
        districtId,
        lessonGroupId,
      )) as unknown as {
        response: { data: Question[] };
      };

      return response?.data?.map(convertQuestionToCamelCase) || {};
    },
    {
      suspense: true,
      enabled: !!lessonGroupId && !!districtId,
      staleTime: 1000 * 60 * 60,
    },
  );

  return queryResponse || {};
};
